import React from "react";
import styled, { keyframes, css } from "styled-components";
import { brand_orange_deep, brand_gradient } from "../../constants/styles";
import useReducedMotion from "../../hooks/useReducedMotion";

// Compact progress indicator: a brand-orange chip with a thin progress
// bar + percentage. Reused on:
//   - the LessonHero (next to the lesson title)
//   - the in-player status row above the video frame
//
// `variant`:
//   - "chip" (default) — pill with bar + label, suitable for headers
//   - "compact" — text-only "62% selesai" pill, no bar (used in tight
//     spots like the sticky context strip)
//   - "ring" — small circular SVG progress with % in the centre; used
//     in the player status bar where horizontal space is at a premium
//
// When the displayed value INCREASES between renders, the label briefly
// pulses (scale + glow) so the user notices the update — every tick
// otherwise just slides the bar via the existing 320 ms transition. The
// pulse is gated by `useReducedMotion` so OS-level motion preferences
// are respected.

function LessonProgressChip({
  percent,
  durationSeconds = 0,
  variant = "chip",
  showMinutes = false,
}) {
  const reduced = useReducedMotion();
  const safePct = typeof percent === "number" ? percent : null;
  const pct =
    safePct === null ? 0 : Math.max(0, Math.min(100, Math.round(safePct)));

  // Re-trigger the keyframes animation whenever `pct` increases. Using a
  // `key` flip on the animated node is the cheapest reliable way to
  // restart a CSS animation from the start; useState + useEffect keeps
  // the bookkeeping in one place.
  const prevRef = React.useRef(pct);
  const [pulseNonce, setPulseNonce] = React.useState(0);
  React.useEffect(() => {
    if (pct > prevRef.current) {
      setPulseNonce((n) => n + 1);
    }
    prevRef.current = pct;
  }, [pct]);

  if (safePct === null) return null;

  const watchedMinutes = Math.round((pct / 100) * (durationSeconds / 60));
  const totalMinutes = Math.max(1, Math.round(durationSeconds / 60));
  const completed = pct >= 100;
  const animate = !reduced;

  if (variant === "compact") {
    return (
      <CompactPill
        $completed={completed}
        $animate={animate}
        key={animate ? `c-${pulseNonce}` : "c-static"}
      >
        {completed ? "✓ Selesai" : `${pct}% selesai`}
      </CompactPill>
    );
  }

  if (variant === "ring") {
    const size = 36;
    const stroke = 4;
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const offset = c - (c * pct) / 100;
    const ariaLabel = completed ? "Selesai" : `${pct}% selesai`;
    // The ring is currently rendered inside the always-dark player
    // status bar — track and label colours are hardcoded for that
    // surface (light track, brand-orange ring, warm label).
    const trackStroke = "rgba(255, 255, 255, 0.16)";
    const ringStroke = completed ? "#34d399" : "#fb923c";
    return (
      <RingWrap
        $completed={completed}
        $animate={animate}
        key={animate ? `r-${pulseNonce}` : "r-static"}
        role="status"
        aria-live="polite"
        aria-label={ariaLabel}
        title={ariaLabel}
      >
        <RingSvg viewBox={`0 0 ${size} ${size}`} width={size} height={size}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={trackStroke}
            strokeWidth={stroke}
          />
          <RingFill
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={ringStroke}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={c}
            style={{ strokeDashoffset: offset }}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
          />
        </RingSvg>
        <RingLabel $completed={completed} aria-hidden="true">
          {completed ? "✓" : pct}
        </RingLabel>
      </RingWrap>
    );
  }

  return (
    <Chip $completed={completed} role="status" aria-live="polite">
      <Bar
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Progres menonton"
      >
        <Fill style={{ width: `${pct}%` }} />
      </Bar>
      <Label
        $animate={animate}
        $completed={completed}
        key={animate ? pulseNonce : "static"}
      >
        {completed ? "Selesai" : `${pct}% selesai`}
        {showMinutes && durationSeconds > 0 && !completed && (
          <Minutes>
            {" · "}
            {watchedMinutes} / {totalMinutes} menit
          </Minutes>
        )}
      </Label>
    </Chip>
  );
}

export default React.memo(LessonProgressChip);

const pulse = keyframes`
  0%   { transform: scale(1);    text-shadow: none; }
  35%  { transform: scale(1.10); text-shadow: 0 0 10px rgba(216, 89, 7, 0.55); }
  100% { transform: scale(1);    text-shadow: none; }
`;

const Chip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 6px 14px;
  border-radius: 999px;
  background: ${({ $completed }) =>
    $completed ? "rgba(16, 185, 129, 0.12)" : "rgba(216, 89, 7, 0.10)"};
  border: 1px solid
    ${({ $completed }) =>
      $completed ? "rgba(16, 185, 129, 0.24)" : "rgba(216, 89, 7, 0.24)"};
  min-width: 180px;

  [data-theme="dark"] & {
    background: ${({ $completed }) =>
      $completed ? "rgba(16, 185, 129, 0.18)" : "rgba(216, 89, 7, 0.18)"};
  }
`;

const Bar = styled.div`
  flex: 1;
  height: 6px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.08);
  overflow: hidden;
  min-width: 80px;

  [data-theme="dark"] & {
    background: rgba(255, 255, 255, 0.12);
  }
`;

const Fill = styled.div`
  height: 100%;
  background: ${brand_gradient};
  transition: width 320ms ease;
`;

const Label = styled.span`
  font-size: 12px;
  font-weight: 800;
  color: ${({ $completed }) => ($completed ? "#047857" : brand_orange_deep)};
  white-space: nowrap;
  letter-spacing: 0.02em;
  display: inline-block;
  transform-origin: center;

  ${({ $animate }) =>
    $animate &&
    css`
      animation: ${pulse} 480ms ease-out;
    `}
`;

const Minutes = styled.span`
  font-weight: 600;
  color: #6b7280;
  letter-spacing: 0;
  [data-theme="dark"] & {
    color: #9ca3af;
  }
`;

const RingWrap = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  transform-origin: center;

  ${({ $animate }) =>
    $animate &&
    css`
      animation: ${pulse} 480ms ease-out;
    `}
`;

const RingSvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`;

const RingFill = styled.circle`
  transition: stroke-dashoffset 320ms ease;
`;

const RingLabel = styled.span`
  position: relative;
  font-size: 11px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
  color: ${({ $completed }) => ($completed ? "#34d399" : "#fdba74")};
  line-height: 1;
`;

const CompactPill = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ $completed }) =>
    $completed ? "rgba(16, 185, 129, 0.16)" : "rgba(216, 89, 7, 0.12)"};
  color: ${({ $completed }) => ($completed ? "#047857" : brand_orange_deep)};
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  transform-origin: center;

  ${({ $animate }) =>
    $animate &&
    css`
      animation: ${pulse} 480ms ease-out;
    `}
`;
